import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Building2, CheckCircle2 } from "lucide-react";
import { Logo } from "../components/Logo";
import { api, formatApiErrorDetail } from "../lib/api";

const PLANS = [
  { id: "starter", label: "Starter", hint: "Up to 100 units" },
  { id: "growth", label: "Growth", hint: "Up to 300 units" },
  { id: "enterprise", label: "Enterprise", hint: "Unlimited" },
];

export default function RegisterSociety() {
  const [form, setForm] = useState({
    name: "", address: "", city: "", admin_name: "", admin_email: "", units: "", plan: "starter",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/societies/register", {
        ...form,
        units: parseInt(form.units, 10),
      });
      setDone(data);
      toast.success(`${data.name} is now registered on SynqSMC ERP`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="register-page" className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-5 py-16">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-gradient-to-b from-sky-100 via-blue-50 to-transparent blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(37,99,235,0.06)_1px,transparent_0)] [background-size:32px_32px]" />
      </div>

      <motion.div
        className="relative w-full max-w-xl"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <Link to="/" data-testid="register-back-home"
              className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600">
          <ArrowLeft size={15} /> Back to homepage
        </Link>

        <div className="card-soft p-8 shadow-lg shadow-slate-200/60 sm:p-10">
          {done ? (
            <div className="text-center" data-testid="register-success">
              <span className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <CheckCircle2 size={30} />
              </span>
              <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
                {done.name} is registered
              </h1>
              <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
                Your society is live on SynqSMC ERP and now appears in the platform's Tenant
                Management list. Our team will reach out to {done.admin_email} to complete onboarding.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/" data-testid="register-success-home" className="btn-ghost">Back home</Link>
                <Link to="/login" data-testid="register-success-login" className="btn-primary">Platform login</Link>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <Building2 size={22} />
                </span>
                <Logo size={40} />
              </div>
              <h1 className="mt-8 text-2xl font-extrabold tracking-tight text-slate-900" data-testid="register-heading">
                Register your society
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                It takes two minutes — your society shows up in Tenant Management instantly.
              </p>

              <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
                <div>
                  <label htmlFor="reg-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Society name</label>
                  <input id="reg-name" data-testid="reg-name-input" className="input-field" placeholder="Green Valley Residency" value={form.name} onChange={set("name")} required minLength={3} />
                </div>
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label htmlFor="reg-address" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Address</label>
                    <input id="reg-address" data-testid="reg-address-input" className="input-field" placeholder="88 Baner Road" value={form.address} onChange={set("address")} required minLength={3} />
                  </div>
                  <div>
                    <label htmlFor="reg-city" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">City</label>
                    <input id="reg-city" data-testid="reg-city-input" className="input-field" placeholder="Pune" value={form.city} onChange={set("city")} required minLength={2} />
                  </div>
                  <div>
                    <label htmlFor="reg-units" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Total units</label>
                    <input id="reg-units" type="number" min="1" data-testid="reg-units-input" className="input-field" placeholder="180" value={form.units} onChange={set("units")} required />
                  </div>
                  <div>
                    <label htmlFor="reg-admin-name" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Admin / manager name</label>
                    <input id="reg-admin-name" data-testid="reg-admin-name-input" className="input-field" placeholder="Sneha Kulkarni" value={form.admin_name} onChange={set("admin_name")} required minLength={2} />
                  </div>
                  <div>
                    <label htmlFor="reg-admin-email" className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Admin email</label>
                    <input id="reg-admin-email" type="email" data-testid="reg-admin-email-input" className="input-field" placeholder="sneha@greenvalley.in" value={form.admin_email} onChange={set("admin_email")} required />
                  </div>
                </div>

                <div>
                  <p className="mb-2 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Plan</p>
                  <div className="grid grid-cols-3 gap-2" data-testid="reg-plan-selector">
                    {PLANS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setForm({ ...form, plan: p.id })}
                        data-testid={`reg-plan-${p.id}`}
                        className={`rounded-xl border px-3 py-3 text-left transition-all duration-200 ${
                          form.plan === p.id
                            ? "border-blue-500 bg-blue-50 ring-2 ring-blue-100"
                            : "border-slate-200 bg-white hover:border-blue-200"
                        }`}
                      >
                        <p className={`text-sm font-bold ${form.plan === p.id ? "text-blue-700" : "text-slate-800"}`}>{p.label}</p>
                        <p className="mt-0.5 text-[11px] text-slate-400">{p.hint}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="register-submit-button"
                  className="btn-primary w-full !py-3.5 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Building2 size={16} />}
                  {busy ? "Registering…" : "Register society"}
                </button>
              </form>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
